<template>
    <div class="lite-diagram-viewer" :class="{ 'fullscreen': fullscreenMode }">
        <div class="diagram-container">
            <div class="diagram-zoom-wrapper" :style="{ transform: `scale(${zoomLevel})` }">
                <svg :width="diagramWidth" :height="diagramHeight" class="diagram-svg">
                <defs>
                    <!-- Block gradients -->
                    <linearGradient id="lite-gradient-start" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style="stop-color:#10b981;stop-opacity:1" />
                        <stop offset="100%" style="stop-color:#059669;stop-opacity:1" />
                    </linearGradient>
                    <linearGradient id="lite-gradient-webhook" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style="stop-color:#3b82f6;stop-opacity:1" />
                        <stop offset="100%" style="stop-color:#2563eb;stop-opacity:1" />
                    </linearGradient>
                    <linearGradient id="lite-gradient-sleep" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style="stop-color:#8b5cf6;stop-opacity:1" />
                        <stop offset="100%" style="stop-color:#7c3aed;stop-opacity:1" />
                    </linearGradient>
                    <linearGradient id="lite-gradient-contact" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style="stop-color:#f59e0b;stop-opacity:1" />
                        <stop offset="100%" style="stop-color:#d97706;stop-opacity:1" />
                    </linearGradient>
                    <linearGradient id="lite-gradient-email" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style="stop-color:#06b6d4;stop-opacity:1" />
                        <stop offset="100%" style="stop-color:#0891b2;stop-opacity:1" />
                    </linearGradient>
                    <linearGradient id="lite-gradient-ai" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style="stop-color:#ec4899;stop-opacity:1" />
                        <stop offset="100%" style="stop-color:#db2777;stop-opacity:1" />
                    </linearGradient>
                    <linearGradient id="lite-gradient-filter" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style="stop-color:#ef4444;stop-opacity:1" />
                        <stop offset="100%" style="stop-color:#dc2626;stop-opacity:1" />
                    </linearGradient>
                    <linearGradient id="lite-gradient-loop" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style="stop-color:#14b8a6;stop-opacity:1" />
                        <stop offset="100%" style="stop-color:#0d9488;stop-opacity:1" />
                    </linearGradient>
                    <linearGradient id="lite-gradient-terminate" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style="stop-color:#6b7280;stop-opacity:1" />
                        <stop offset="100%" style="stop-color:#4b5563;stop-opacity:1" />
                    </linearGradient>
                    <linearGradient id="lite-gradient-success" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style="stop-color:#22c55e;stop-opacity:1" />
                        <stop offset="100%" style="stop-color:#16a34a;stop-opacity:1" />
                    </linearGradient>
                    <linearGradient id="lite-gradient-failure" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style="stop-color:#f87171;stop-opacity:1" />
                        <stop offset="100%" style="stop-color:#ef4444;stop-opacity:1" />
                    </linearGradient>
                    <linearGradient id="lite-gradient-default" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style="stop-color:#9ca3af;stop-opacity:1" />
                        <stop offset="100%" style="stop-color:#6b7280;stop-opacity:1" />
                    </linearGradient>
                </defs>

                <!-- Normal Connections -->
                <g class="connections">
                    <path
                        v-for="(connection, index) in connections"
                        :key="`connection-${index}`"
                        :d="connection.path"
                        :class="[
                            'connection-line',
                            { 
                                'executed': isConnectionExecuted(connection),
                                'current': isConnectionCurrent(connection)
                            }
                        ]"
                        stroke="#94a3b8"
                        stroke-width="2"
                        fill="none"
                        marker-end="url(#arrowhead)"
                    />
                </g>


                <!-- Arrow marker -->
                <defs>
                    <!-- Arrow markers -->
                    <marker id="arrowhead" markerWidth="10" markerHeight="7" 
                        refX="9" refY="3.5" orient="auto">
                        <polygon points="0 0, 10 3.5, 0 7" fill="#94a3b8" />
                    </marker>
                    <marker id="arrowhead-executed" markerWidth="8" markerHeight="6" 
                        refX="7" refY="3" orient="auto">
                        <polygon points="0 0, 8 3, 0 6" fill="#10b981" />
                    </marker>
                    <marker id="arrowhead-current" markerWidth="10" markerHeight="7" 
                        refX="9" refY="3.5" orient="auto">
                        <polygon points="0 0, 10 3.5, 0 7" fill="#3b82f6" />
                    </marker>
                    
                    <!-- Glow filters -->
                    <filter id="glow-current" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                        <feMerge> 
                            <feMergeNode in="coloredBlur"/>
                            <feMergeNode in="SourceGraphic"/>
                        </feMerge>
                    </filter>
                    
                    <filter id="glow-highlighted" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                        <feMerge> 
                            <feMergeNode in="coloredBlur"/>
                            <feMergeNode in="SourceGraphic"/>
                        </feMerge>
                    </filter>
                    
                    <!-- Animated gradient for current position -->
                    <linearGradient id="current-position-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style="stop-color:#60a5fa;stop-opacity:0.8">
                            <animate attributeName="stop-color" 
                                values="#60a5fa;#3b82f6;#1d4ed8;#3b82f6;#60a5fa" 
                                dur="2s" repeatCount="indefinite"/>
                        </stop>
                        <stop offset="100%" style="stop-color:#1d4ed8;stop-opacity:0.9">
                            <animate attributeName="stop-color" 
                                values="#1d4ed8;#1e40af;#1e3a8a;#1e40af;#1d4ed8" 
                                dur="2s" repeatCount="indefinite"/>
                        </stop>
                    </linearGradient>
                </defs>

                <!-- Blocks -->
                <g class="blocks">
                    <g
                        v-for="block in positionedBlocks"
                        :key="block.id"
                        :class="[
                            'block-group',
                            {
                                'highlighted': isBlockHighlighted(block.id),
                                'executed': isBlockExecuted(block.id),
                                'current': isBlockCurrent(block.id)
                            }
                        ]"
                        @click="handleBlockClick(block)"
                    >
                        <!-- Glow background for highlighted/current blocks -->
                        <rect
                            v-if="isBlockCurrent(block.id) || isBlockHighlighted(block.id)"
                            :x="block.x - 8"
                            :y="block.y - 8"
                            :width="block.width + 16"
                            :height="block.height + 16"
                            :fill="isBlockCurrent(block.id) ? '#dc2626' : '#3b82f6'"
                            opacity="0.2"
                            rx="12"
                            ry="12"
                            class="block-glow"
                        />
                        
                        <!-- Main block rectangle -->
                        <rect
                            :x="block.x"
                            :y="block.y"
                            :width="block.width"
                            :height="block.height"
                            :fill="getBlockFill(block)"
                            :stroke="getBlockStroke(block.id)"
                            :stroke-width="getBlockStrokeWidth(block.id)"
                            :filter="getBlockFilter(block.id)"
                            :opacity="getBlockOpacity(block.id)"
                            rx="6"
                            ry="6"
                            class="block-rect"
                            
                        />
                        
                        <!-- Value indicator (sol üst köşe) -->
                        <circle
                            v-if="hasBlockValue(block.id)"
                            :cx="block.x + 15"
                            :cy="block.y + 15"
                            r="4"
                            fill="#10b981"
                            class="value-indicator"
                        />
                        <text
                            v-if="hasBlockValue(block.id)"
                            :x="block.x + 15"
                            :y="block.y + 18"
                            text-anchor="middle"
                            class="value-indicator-text"
                            fill="white"
                            font-size="8"
                            font-weight="600"
                        >●</text>
                        
                        <!-- Current position (final position) indicator -->
                        <g v-if="isBlockCurrent(block.id)" class="current-indicator">
                            <!-- Stop icon -->
                            <text
                                :x="block.x - 15"
                                :y="block.y + block.height/2 + 4"
                                text-anchor="middle"
                                class="current-stop-icon"
                                fill="#dc2626"
                                font-size="16"
                            >⏹️</text>
                            
                            <!-- "Otomasyon burada sonlandırıldı" text -->
                            <text
                                :x="block.x + block.width/2"
                                :y="block.y - 12"
                                text-anchor="middle"
                                class="current-text"
                                fill="#dc2626"
                                font-size="11"
                                font-weight="600"
                            >🔴 Otomasyon burada sonlandırıldı</text>
                        </g>
                        
                        <!-- Highlight indicator (traced block) -->
                        <g v-if="isBlockHighlighted(block.id)" class="highlight-indicator">
                            <!-- Location pin icon -->
                            <text
                                :x="block.x - 15"
                                :y="block.y + block.height/2 + 4"
                                text-anchor="middle"
                                class="highlight-location-icon"
                                fill="#3b82f6"
                                font-size="16"
                            >📍</text>
                            
                            <!-- "Şuan bu konumda" text -->
                            <text
                                :x="block.x + block.width/2"
                                :y="block.y - 12"
                                text-anchor="middle"
                                class="highlight-text"
                                fill="#3b82f6"
                                font-size="11"
                                font-weight="600"
                            >📍 Şuan bu konumda</text>
                        </g>
                        
                        <!-- Block icon -->
                        <text
                            :x="block.x + 12"
                            :y="block.y + block.height/2 + 6"
                            class="block-icon"
                            :fill="getBlockTextColor(block.type)"
                            :opacity="getBlockOpacity(block.id)"
                        >{{ getBlockIcon(block.type) }}</text>
                        
                        <!-- Block name -->
                        <text
                            :x="block.x + 32"
                            :y="block.y + block.height/2 + 4"
                            class="block-name"
                            :fill="getBlockTextColor(block.type)"
                            :opacity="getBlockOpacity(block.id)"
                        >{{ getBlockDisplayName(block) }}</text>
                        
                        <!-- Execution order badge -->
                        <circle
                            v-if="getExecutionOrder(block.id) > 0"
                            :cx="block.x + block.width - 10"
                            :cy="block.y + 10"
                            r="8"
                            fill="#3b82f6"
                            class="execution-badge"
                        />
                        <text
                            v-if="getExecutionOrder(block.id) > 0"
                            :x="block.x + block.width - 10"
                            :y="block.y + 14"
                            class="execution-order"
                            fill="white"
                            text-anchor="middle"
                        >{{ getExecutionOrder(block.id) }}</text>
                    </g>
                </g>
                </svg>
            </div>
        </div>
        
        <!-- Toolbar -->
        <div class="diagram-toolbar">
            <div class="toolbar-left">
                <!-- Payload button -->
                <a-button 
                    v-if="showPayloadButton" 
                    size="small" 
                    type="default"
                    @click="$emit('showPayload')"
                >
                    <i class="material-icons">code</i>
                    Plan Payload
                </a-button>
            </div>
            <div class="toolbar-right">
                <!-- Zoom controls -->
                <a-button-group>
                    <a-button 
                        size="small" 
                        type="default"
                        @click="zoomOut"
                        title="Uzaklaştır"
                    >
                        <i class="material-icons">zoom_out</i>
                    </a-button>
                    <a-button 
                        size="small" 
                        type="default"
                        @click="resetZoom"
                        title="Zoom Sıfırla"
                    >
                        <i class="material-icons">center_focus_strong</i>
                    </a-button>
                    <a-button 
                        size="small" 
                        type="default"
                        @click="zoomIn"
                        title="Yakınlaştır"
                    >
                        <i class="material-icons">zoom_in</i>
                    </a-button>
                </a-button-group>
                
                <!-- Fullscreen button -->
                <a-button 
                    size="small" 
                    type="default"
                    @click="toggleFullscreen"
                    :title="fullscreenMode ? 'Tam Ekrandan Çık' : 'Tam Ekran'"
                >
                    <i class="material-icons">
                        {{ fullscreenMode ? 'fullscreen_exit' : 'fullscreen' }}
                    </i>
                </a-button>
            </div>
        </div>
        
        <!-- Legend -->
        <div class="diagram-legend">
            <div class="legend-item">
                <div class="legend-color current"></div>
                <span>🔴 Sonlandırıldığı Konum</span>
            </div>
            <div class="legend-item">
                <div class="legend-color executed"></div>
                <span>✅ Çalıştırılan Block</span>
            </div>
            <div class="legend-item">
                <div class="legend-color highlighted"></div>
                <span>📍 Trace Edilen Block</span>
            </div>
        </div>
    </div>
</template>

<script>
import * as d3 from "d3";

export default {
    name: "LiteDiagramViewer",
    props: {
        diagram: {
            type: Object,
            required: true,
        },
        highlightedBlocks: {
            type: Array,
            default: () => [],
        },
        executionHistory: {
            type: Array,
            default: () => [],
        },
        currentPosition: {
            type: String,
            default: null,
        },
        autoFocus: {
            type: Boolean,
            default: true,
        },
        blockValues: {
            type: Object,
            default: () => ({}),
        },
        showPayloadButton: {
            type: Boolean,
            default: false,
        },
        fullscreenMode: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            diagramWidth: 800,
            diagramHeight: 600,
            blockWidth: 190,
            blockHeight: 40,
            zoomLevel: 1,
            minZoom: 0.5,
            maxZoom: 2,
        };
    },
    computed: {
        flatBlocks() {
            const blocks = [];
            const flatten = (node, level = 0, parentId = null) => {
                if (!node) return;
                
                blocks.push({
                    ...node,
                    level,
                    parentId,
                });
                
                if (node.children && Array.isArray(node.children)) {
                    node.children.forEach(child => {
                        flatten(child, level + 1, node.id);
                    });
                }
            };
            
            flatten(this.diagram);
            return blocks;
        },
        positionedBlocks() {
            if (!this.diagram || !this.diagram.id) return [];
            
            // D3 tree layout kullanarak pozisyonları hesapla
            
            // Hierarchy oluştur
            const root = d3.hierarchy(this.diagram, (d) => {
                return d.children && d.children.length > 0 ? d.children : null;
            });
            
            // Tree layout
            const treeLayout = d3.tree()
                .nodeSize([this.blockHeight * 2.5, this.blockWidth + 80]);
            
            const treeData = treeLayout(root);
            
            // Pozisyonları blocks array'ine map et
            const blocks = [];
            
            const traverse = (node) => {
                if (node.data) {
                    blocks.push({
                        ...node.data,
                        x: node.y + 50, // D3'te y horizontal pozisyon
                        y: node.x + 300, // D3'te x vertical pozisyon
                        width: this.blockWidth,
                        height: this.blockHeight,
                    });
                }
                
                if (node.children) {
                    node.children.forEach(traverse);
                }
            };
            
            traverse(treeData);
            
            // Diagram boyutlarını güncelle
            if (blocks.length > 0) {
                const maxX = Math.max(...blocks.map(b => b.x + b.width)) + 50;
                const maxY = Math.max(...blocks.map(b => b.y + b.height)) + 50;
                const minY = Math.min(...blocks.map(b => b.y)) - 50;
                
                this.diagramWidth = Math.max(800, maxX);
                this.diagramHeight = Math.max(600, maxY - minY);
                
                // Negatif Y değerlerini düzelt
                if (minY < 0) {
                    blocks.forEach(block => {
                        block.y += Math.abs(minY) + 50;
                    });
                }
            }
            
            return blocks;
        },
        connections() {
            const connections = [];
            
            this.positionedBlocks.forEach(block => {
                if (block.children && Array.isArray(block.children)) {
                    block.children.forEach(child => {
                        const childBlock = this.positionedBlocks.find(b => b.id === child.id);
                        if (childBlock) {
                            const startX = block.x + block.width;
                            const startY = block.y + block.height / 2;
                            const endX = childBlock.x;
                            const endY = childBlock.y + childBlock.height / 2;
                            
                            // Curved connection path (AutomationDiagramViewer benzeri)
                            const midX = startX + (endX - startX) / 2;
                            const path = `M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${endY}, ${endX} ${endY}`;
                            
                            connections.push({
                                path,
                                from: block.id,
                                to: childBlock.id,
                            });
                        }
                    });
                }
            });
            
            return connections;
        },
    },
    methods: {
        getBlockColor(type) {
            const colors = {
                'START_POINT': 'url(#lite-gradient-start)',
                'WEBHOOK': 'url(#lite-gradient-webhook)',
                'SLEEP': 'url(#lite-gradient-sleep)',
                'CREATE_CONTACT': 'url(#lite-gradient-contact)',
                'UPDATE_CONTACT': 'url(#lite-gradient-contact)',
                'SEND_EMAIL': 'url(#lite-gradient-email)',
                'SEND_SMS': 'url(#lite-gradient-email)',
                'ASK_AI': 'url(#lite-gradient-ai)',
                'FILTER': 'url(#lite-gradient-filter)',
                'LOOP': 'url(#lite-gradient-loop)',
                'TERMINATE_AUTOMATION': 'url(#lite-gradient-terminate)',
                'SUCCESS': 'url(#lite-gradient-success)',
                'FAILURE': 'url(#lite-gradient-failure)',
                'DEFAULT_FALLBACK': 'url(#lite-gradient-default)',
                'TIMEOUT': 'url(#lite-gradient-default)',
                'RUN_JAVASCRIPT': 'url(#lite-gradient-ai)',
                'NOOP': 'url(#lite-gradient-default)',
            };
            return colors[type] || 'url(#lite-gradient-default)';
        },
        getBlockTextColor(type) {
            return '#ffffff';
        },
        getBlockIcon(type) {
            const icons = {
                'START_POINT': '▶',
                'WEBHOOK': '🔗',
                'SLEEP': '⏳',
                'CREATE_CONTACT': '👤',
                'UPDATE_CONTACT': '✏️',
                'SEND_EMAIL': '📧',
                'SEND_SMS': '📱',
                'ASK_AI': '🤖',
                'FILTER': '🔍',
                'LOOP': '🔄',
                'TERMINATE_AUTOMATION': '⏹️',
                'SUCCESS': '✅',
                'FAILURE': '❌',
                'DEFAULT_FALLBACK': '⚠️',
                'TIMEOUT': '⏰',
                'RUN_JAVASCRIPT': '⚡',
                'NOOP': '⚪',
            };
            return icons[type] || '⚪';
        },
        getBlockDisplayName(block) {
            if (block.name && block.name.trim()) {
                return block.name.length > 15 ? block.name.substring(0, 15) + '...' : block.name;
            }
            
            const typeNames = {
                'START_POINT': 'Başlangıç',
                'WEBHOOK': 'Webhook',
                'SLEEP': 'Bekleme',
                'CREATE_CONTACT': 'Kişi Oluştur',
                'UPDATE_CONTACT': 'Kişi Güncelle',
                'SEND_EMAIL': 'E-posta',
                'SEND_SMS': 'SMS',
                'ASK_AI': 'AI Sor',
                'FILTER': 'Filtre',
                'LOOP': 'Döngü',
                'TERMINATE_AUTOMATION': 'Sonlandır',
                'SUCCESS': 'Başarılı',
                'FAILURE': 'Başarısız',
                'DEFAULT_FALLBACK': 'Fallback',
                'TIMEOUT': 'Zaman Aşımı',
                'RUN_JAVASCRIPT': 'JavaScript',
                'NOOP': 'Hiçbir Şey Yapma',
            };
            
            const displayName = typeNames[block.type] || block.type;
            return displayName.length > 15 ? displayName.substring(0, 15) + '...' : displayName;
        },
        isBlockHighlighted(blockId) {
            return this.highlightedBlocks.includes(blockId);
        },
        isBlockExecuted(blockId) {
            return this.executionHistory.includes(blockId);
        },
        isBlockCurrent(blockId) {
            return this.currentPosition === blockId;
        },
        getBlockStroke(blockId) {
            if (this.isBlockCurrent(blockId)) return '#dc2626';
            if (this.isBlockHighlighted(blockId)) return '#3b82f6';
            if (this.isBlockExecuted(blockId)) return '#10b981';
            return 'rgba(255, 255, 255, 0.3)';
        },
        getBlockStrokeWidth(blockId) {
            if (this.isBlockCurrent(blockId)) return '4';
            if (this.isBlockHighlighted(blockId)) return '3';
            if (this.isBlockExecuted(blockId)) return '2';
            return '1';
        },
        getBlockFill(block) {
            // Current position için normal renk kullan, sadece border ve glow ile vurgula
            return this.getBlockColor(block.type);
        },
        getBlockFilter(blockId) {
            if (this.isBlockCurrent(blockId)) return 'url(#glow-current)';
            if (this.isBlockHighlighted(blockId)) return 'url(#glow-highlighted)';
            return null;
        },
        getBlockOpacity(blockId) {
            // Çalıştırılmayan block'lar şeffaf
            if (this.isBlockExecuted(blockId) || this.isBlockCurrent(blockId) || this.isBlockHighlighted(blockId)) {
                return 1.0;
            }
            return 0.3; // Çalıştırılmayan block'lar çok şeffaf
        },
        getExecutionOrder(blockId) {
            const index = this.executionHistory.indexOf(blockId);
            return index >= 0 ? index + 1 : 0;
        },
        isConnectionExecuted(connection) {
            return this.isBlockExecuted(connection.from) && this.isBlockExecuted(connection.to);
        },
        isConnectionCurrent(connection) {
            return this.currentPosition === connection.from;
        },
        hasBlockValue(blockId) {
            const value = this.blockValues[blockId];
            return value !== null && value !== undefined && value !== '';
        },
        handleBlockClick(block) {
            // Başlangıç block'u için özel davranış
            if (block.type === 'START_POINT') {
                this.$emit('showPayload');
                return;
            }
            
            this.$emit('blockClicked', {
                blockId: block.id,
                blockName: this.getBlockDisplayName(block),
                blockValue: this.blockValues[block.id],
                hasValue: this.hasBlockValue(block.id)
            });
        },
        toggleFullscreen() {
            this.$emit('toggleFullscreen');
        },
        zoomIn() {
            if (this.zoomLevel < this.maxZoom) {
                this.zoomLevel = Math.min(this.maxZoom, this.zoomLevel + 0.25);
            }
        },
        zoomOut() {
            if (this.zoomLevel > this.minZoom) {
                this.zoomLevel = Math.max(this.minZoom, this.zoomLevel - 0.25);
            }
        },
        resetZoom() {
            this.zoomLevel = 1;
            // Auto-focus'u da tekrar çalıştır
            this.$nextTick(() => {
                this.focusOnExecutedBlocks();
            });
        },
        focusOnExecutedBlocks() {
            if (!this.autoFocus || this.executionHistory.length === 0) return;
            
            // Son çalıştırılan block'u bul
            const lastExecutedBlockId = this.executionHistory[this.executionHistory.length - 1];
            const lastExecutedBlock = this.positionedBlocks.find(b => b.id === lastExecutedBlockId);
            
            if (lastExecutedBlock) {
                this.$nextTick(() => {
                    // SVG container'ını bul
                    const container = this.$el.querySelector('.diagram-container');
                    if (container) {
                        // Zoom level'ı da hesaba kat
                        const blockCenterX = (lastExecutedBlock.x + lastExecutedBlock.width / 2) * this.zoomLevel;
                        const blockCenterY = (lastExecutedBlock.y + lastExecutedBlock.height / 2) * this.zoomLevel;
                        
                        // Container'ın merkez konumunu hesapla
                        const containerCenterX = container.clientWidth / 2;
                        const containerCenterY = container.clientHeight / 2;
                        
                        // Scroll pozisyonunu hesapla
                        const scrollLeft = blockCenterX - containerCenterX;
                        const scrollTop = blockCenterY - containerCenterY;
                        
                        // Smooth scroll
                        container.scrollTo({
                            left: Math.max(0, scrollLeft),
                            top: Math.max(0, scrollTop),
                            behavior: 'smooth'
                        });
                    }
                });
            }
        },
        calculatePathLength(pathString) {
            // Path uzunluğunu yaklaşık hesapla (dash animation için)
            const matches = pathString.match(/[\d.]+/g);
            if (!matches || matches.length < 4) return 100;
            
            const startX = parseFloat(matches[0]);
            const startY = parseFloat(matches[1]);
            const endX = parseFloat(matches[matches.length - 2]);
            const endY = parseFloat(matches[matches.length - 1]);
            
            const dx = endX - startX;
            const dy = endY - startY;
            return Math.sqrt(dx * dx + dy * dy) * 1.2; // Curved path için 1.2x
        },
    },
    mounted() {
        // Auto-focus özelliği
        this.$nextTick(() => {
            this.focusOnExecutedBlocks();
        });
    },
};
</script>

<style lang="scss" scoped>
.lite-diagram-viewer {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    
    &.fullscreen {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        z-index: 9999 !important;
        background: white !important;
        padding: 20px;
        box-sizing: border-box;
    }
}

.diagram-container {
    flex: 1;
    overflow: auto;
    background: linear-gradient(rgba(214, 214, 219, 0.2) 1px, transparent 1px),
        linear-gradient(to right, rgba(214, 214, 219, 0.4) 1px, rgba(139, 140, 140, 0.1) 1px);
    background-size: 20px 20px;
    background-color: #f8fafc;
    border-radius: 8px;
    border: 1px solid var(--color-border-default);
    
    .diagram-zoom-wrapper {
        transform-origin: center center;
        transition: transform 0.4s ease-out;
        display: inline-block;
        width: max-content;
        height: max-content;
    }
    
    .diagram-svg {
        display: block;
        background: transparent;
        
        .block-group {
            cursor: pointer;
            transition: all 0.3s ease;
            
            &:hover {
                .block-rect {
                    filter: brightness(1.1);
                }
            }
            
            
            &.executed {
                .block-rect {
                    filter: brightness(1.05);
                }
            }
        }
        
        .block-icon {
            font-size: 16px;
            font-weight: 600;
        }
        
        .block-name {
            font-size: 13px;
            font-weight: 600;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
        
        .execution-order {
            font-size: 10px;
            font-weight: 600;
        }
        
        .block-rect {
            cursor: pointer;
            
            &:hover {
                filter: brightness(1.1);
            }
        }
        
        .connection-line {
            transition: all 0.2s ease;
            
            &.executed {
                stroke: #10b981;
                stroke-width: 3;
                marker-end: url(#arrowhead-executed);
            }
            
            &.current {
                stroke: #3b82f6;
                stroke-width: 3;
                marker-end: url(#arrowhead-current);
                stroke-dasharray: 5,5;
                animation: dash 1s linear infinite;
            }
        }
        
    }
}

.diagram-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: var(--color-bg-default);
    border: 1px solid var(--color-border-default);
    border-radius: 6px;
    
    .toolbar-left,
    .toolbar-right {
        display: flex;
        gap: 8px;
        align-items: center;
    }
}

.diagram-legend {
    display: flex;
    gap: 24px;
    justify-content: center;
    padding: 12px;
    background: var(--color-bg-default);
    border-radius: 8px;
    border: 1px solid var(--color-border-default);
    
    .legend-item {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 12px;
        color: var(--color-text-secondary);
        
        .legend-color {
            width: 12px;
            height: 12px;
            border-radius: 3px;
            
            &.current {
                background: #dc2626;
            }
            
            &.executed {
                background: #10b981;
            }
            
            &.highlighted {
                background: #3b82f6;
            }
        }
    }
}

@keyframes pulse {
    0%, 100% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.02);
    }
}

@keyframes dash {
    to {
        stroke-dashoffset: -10;
    }
}



</style>
